// Instagram integration guide component using content from integrating-with-instagram.md
import { AlertTriangle, CheckCircle, Facebook, Instagram, Play, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { InstagramPrerequisiteWizard } from './InstagramPrerequisiteWizard';

interface InstagramIntegrationGuideProps {
  onBack?: () => void;
}

export function InstagramIntegrationGuide({ onBack }: InstagramIntegrationGuideProps) {
  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        {onBack && (
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        )}
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 flex items-center justify-center">
            <Instagram className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">How to Integrate Instagram with Rainfactory</h1>
            <p className="text-muted-foreground">Connect your Instagram Business account to schedule and publish content</p>
          </div>
        </div>
      </div>

      {/* Important Notice */}
      <Alert className="bg-yellow-50 border-yellow-200">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-800">
          <strong>Important:</strong> Instagram scheduling only works with <strong>Instagram Business Accounts</strong> linked to a <strong>Facebook Page</strong>.
        </AlertDescription>
      </Alert>

      {/* Prerequisite Wizard */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Prerequisite Wizard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4 text-muted-foreground">
            This wizard will help you check and set up the prerequisites needed to connect Instagram to Rainfactory. 
            It takes about <strong>15 minutes</strong> if you need to complete all steps.
          </p>
          <InstagramPrerequisiteWizard />
        </CardContent>
      </Card>

      {/* Integration Steps */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Play className="h-5 w-5 text-blue-600" />
            Integration Steps
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Step 1 */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Badge className="bg-blue-600">1</Badge>
              <h3 className="font-semibold">Open Rainfactory</h3>
            </div>
            <p className="ml-8 text-muted-foreground">Navigate to your <strong>Rainfactory homepage</strong>.</p>
          </div>

          <Separator />

          {/* Step 2 */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Badge className="bg-blue-600">2</Badge>
              <h3 className="font-semibold">Go to Brand Settings</h3>
            </div>
            <div className="ml-8 space-y-2">
              <p className="text-muted-foreground">Navigate to your brand's settings page.</p>
              <p className="text-muted-foreground">Find <strong>Instagram Business</strong>.</p>
              <p className="text-muted-foreground">Click <strong>Connect</strong>.</p>
            </div>
          </div>

          <Separator />

          {/* Step 3 */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Badge className="bg-blue-600">3</Badge>
              <h3 className="font-semibold">Log in to Facebook</h3>
            </div>
            <div className="ml-8 space-y-2">
              <p className="text-muted-foreground">If you're not already logged in to Facebook, you'll be prompted to log in.</p>
              <p className="text-muted-foreground">Continue as your personal Facebook account.</p>
            </div>
          </div>

          <Separator />

          {/* Step 4 */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Badge className="bg-blue-600">4</Badge>
              <h3 className="font-semibold">Log in to Instagram</h3>
            </div>
            <p className="ml-8 text-muted-foreground">Enter your Instagram Business account credentials.</p>
          </div>

          <Separator />

          {/* Step 5 */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Badge className="bg-blue-600">5</Badge>
              <h3 className="font-semibold">Set Permissions</h3>
            </div>
            <div className="ml-8 space-y-3">
              <p className="text-muted-foreground">Select:</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Opt into all current and future businesses</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Opt into all current and future Facebook pages</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Opt into all current and future Instagram accounts</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">These permissions simplify setup and reduce errors.</p>
            </div>
          </div>

          <Separator />

          {/* Step 6 */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Badge className="bg-blue-600">6</Badge>
              <h3 className="font-semibold">Review and Confirm</h3>
            </div>
            <div className="ml-8 space-y-2">
              <p className="text-muted-foreground">Review the permissions you've granted Rainfactory.</p>
              <p className="text-muted-foreground">Click <strong>Save</strong>.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Finalize Connection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Facebook className="h-5 w-5 text-blue-600" />
            Finalize the Connection in Rainfactory
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p>Return to <strong>Rainfactory</strong>.</p>
          <p>Select the specific <strong>Instagram Business Account</strong> you want to connect.</p>
          <p>Click <strong>Connect</strong>.</p>
          <p className="text-sm text-muted-foreground">
            Example: Connect your account like <code className="bg-gray-100 px-1 rounded">@AIforMarketers</code>.
          </p>
        </CardContent>
      </Card>

      {/* Success Message */}
      <Card className="bg-green-50 border-green-200">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle className="h-6 w-6 text-green-600" />
            <h3 className="text-lg font-semibold text-green-800">You're Done!</h3>
          </div>
          <p className="text-green-700 mb-3">You can now:</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-green-700"><strong>Create Instagram content in Rainfactory</strong></span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-green-700"><strong>Schedule or publish posts directly from Rainfactory</strong></span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Troubleshooting */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Troubleshooting Tips
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4">If something isn't working, check:</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span>Is your Instagram account a <strong>Business Account</strong>?</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span>Is it properly <strong>connected to a Facebook Page</strong>?</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span>Are all <strong>permissions granted</strong> through your Meta Business account?</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Need Help? */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <h3 className="text-lg font-semibold mb-3">Still need help?</h3>
          <p className="text-gray-700">
            If you still need assistance with the Instagram integration, click the <strong>'Help'</strong> button in the nav menu and send us a note. We're happy to help!
          </p>
        </CardContent>
      </Card>
    </div>
  );
} 